import { ToastrService } from 'ngx-toastr';
import { Inject, Injectable } from '@angular/core';
import { findIndex, forEach, map, reduce, slice } from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map as rxMap, switchMap, take, tap } from 'rxjs/operators';

import { FetchPolicy } from '@apollo/client/core';
import { IPagination } from '@diktup/frontend/models';
import { StorageHelper } from '@diktup/frontend/helpers';
import { MarketPlaceOrderDtoType } from '@sifca-monorepo/ecommerce-generator';
import {
  CouponType,
  EmptyCartGQL,
  InvoicePdfType,
  PaginationInput,
  RedeemCouponGQL,
  ShoppingCartType,
  CartProductInput,
  IsCouponValidGQL,
  RemoveFromCartGQL,
  IsCouponValidType,
  DeliveryZonesType,
  DiscountDtoInput,
  InternalProductType,
  EmptyTargetCartGQL,
  AddProductsToCartGQL,
  AddDiscountToCartGQL,
  MarketPlaceOrderInput,
  OrderSettingsFullType,
  GetOrderInstallmentGQL,
  InstallmentPaymentInput,
  SuccessResponseDtoType,
  CalculateShoppingCartGQL,
  GetTargetOrderInvoiceGQL,
  CreateMarketPlaceOrderGQL,
  CancelProductByConsumerGQL,
  CreateTargetOrderByCartGQL,
  CalculatedShoppingCartType,
  SendOrderByEmailAndSmsGQL,
  GetOrderSettingsByTargetCacheGQL,
  MarketPlaceOrderProductInput,
  FindCouponByTargetAndCodeGQL,
  MarketPlaceOrderPaginateType,
  FindDeliveryZonesByTargetCacheGQL,
  MarketPlaceOrderWithLoginType,
  SuccessResponseWithOrderIdType,
  FindTargetOrdersByCurrentUserGQL,
  FindShoppingCartByUserAndTargetGQL,
  PayMarketplaceOrderInstallmentsGQL,
  MarketPlaceOrderFilterConsumerInput,
  CreateTargetOrderWithCartForGuestUserGQL,
  CreateTargetOrderWithCartAndRegisterUserGQL,
} from '@sifca-monorepo/ecommerce-generator';

import { AppService } from '../../app.service';
import { PosService } from '../../pos/pos.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private coupon: BehaviorSubject<string> = new BehaviorSubject(null);
  private authenticated: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private isLastProduct: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private pagination: BehaviorSubject<IPagination> = new BehaviorSubject(null);
  private productCart: BehaviorSubject<ShoppingCartType> = new BehaviorSubject(null);
  private order: BehaviorSubject<MarketPlaceOrderDtoType> = new BehaviorSubject(null);
  private products: BehaviorSubject<InternalProductType[]> = new BehaviorSubject(null);
  private deliveryZones: BehaviorSubject<DeliveryZonesType[]> = new BehaviorSubject(null);
  private selectedSpecs: BehaviorSubject<string[]> = new BehaviorSubject(null);
  private orderSettings: BehaviorSubject<OrderSettingsFullType> = new BehaviorSubject(null);
  private orders: BehaviorSubject<MarketPlaceOrderDtoType[]> = new BehaviorSubject<MarketPlaceOrderDtoType[]>([]);
  private calculatedShoppingCart: BehaviorSubject<CalculatedShoppingCartType> = new BehaviorSubject<CalculatedShoppingCartType>(null);

  orderPage = 0;
  orderLimit = 10;
  selectedCoupon: CouponType;

  get calculatedShoppingCart$(): Observable<CalculatedShoppingCartType> {
    return this.calculatedShoppingCart.asObservable();
  }

  get coupon$(): Observable<string> {
    return this.coupon.asObservable();
  }
  set coupon$(value: any) {
    this.coupon.next(value);
  }

  get selectedSpecs$(): Observable<string[]> {
    return this.selectedSpecs.asObservable();
  }
  set selectedSpecs$(value: any) {
    this.selectedSpecs.next(value);
  }
  get deliveryZones$(): Observable<DeliveryZonesType[]> {
    return this.deliveryZones.asObservable();
  }
  get orderSettings$(): Observable<OrderSettingsFullType> {
    return this.orderSettings.asObservable();
  }
  get authenticated$(): Observable<boolean> {
    return this.authenticated.asObservable();
  }
  get orders$(): Observable<MarketPlaceOrderDtoType[]> {
    return this.orders.asObservable();
  }
  set orders$(value: any) {
    this.orders.next(value);
  }
  get order$(): Observable<MarketPlaceOrderDtoType> {
    return this.order.asObservable();
  }
  set order$(value: any) {
    this.order.next(value);
  }
  get products$(): Observable<InternalProductType[]> {
    return this.products.asObservable();
  }
  get isLastProduct$(): Observable<boolean> {
    return this.isLastProduct.asObservable();
  }
  get pagination$(): Observable<IPagination> {
    return this.pagination.asObservable();
  }
  get productCart$(): Observable<ShoppingCartType> {
    return this.productCart.asObservable();
  }
  set productCart$(value: any) {
    this.productCart.next(value);
  }

  constructor(
    private appService: AppService,
    private posService: PosService,
    private emptyCartGQL: EmptyCartGQL,
    private storageHelper: StorageHelper,
    protected toastrService: ToastrService,
    private redeemCouponGQL: RedeemCouponGQL,
    private isCouponValidGQL: IsCouponValidGQL,
    private removeFromCartGQL: RemoveFromCartGQL,
    private emptyTargetCartGQL: EmptyTargetCartGQL,
    private addProductsToCartGQL: AddProductsToCartGQL,
    private addDiscountToCartGQL: AddDiscountToCartGQL,
    private getOrderInstallmentGQL: GetOrderInstallmentGQL,
    private calculateShoppingCartGQL: CalculateShoppingCartGQL,
    private getTargetOrderInvoiceGQL: GetTargetOrderInvoiceGQL,
    private createMarketPlaceOrderGQL: CreateMarketPlaceOrderGQL,
    private sendOrderByEmailAndSmsGQL: SendOrderByEmailAndSmsGQL,
    private cancelProductByConsumerGQL: CancelProductByConsumerGQL,
    private createTargetOrderByCartGQL: CreateTargetOrderByCartGQL,
    private getOrderSettingsByTargetGQL: GetOrderSettingsByTargetCacheGQL,
    private findDeliveryZonesByTargetGQL: FindDeliveryZonesByTargetCacheGQL,
    private findCouponByTargetAndCodeGQL: FindCouponByTargetAndCodeGQL,
    private findTargetOrdersByCurrentUserGQL: FindTargetOrdersByCurrentUserGQL,
    private payMarketplaceOrderInstallmentsGQL: PayMarketplaceOrderInstallmentsGQL,
    private findShoppingCartByUserAndTargetGQL: FindShoppingCartByUserAndTargetGQL,
    private createTargetOrderWithCartForGuestUserGql: CreateTargetOrderWithCartForGuestUserGQL,
    private createTargetOrderWithCartAndRegisterUserGql: CreateTargetOrderWithCartAndRegisterUserGQL,
    @Inject('ACCESS_TOKEN') private ACCESS_TOKEN: string,
  ) {}

  createTargetOrderWithCartAndRegisterUser(input): Observable<MarketPlaceOrderWithLoginType> {
    return this.createTargetOrderWithCartAndRegisterUserGql
      .mutate({
        input: {
          ...input,
          currency: {
            id: '651afcdde5ecd0565a7b0f91',
            name: 'TND'
          },
          target: {
            pos: this.storageHelper.getData('posId'),
          },
        },
      })
      .pipe(
        take(1),
        rxMap(({ data }: any) => {
          this.storageHelper.setData({
            currentUserId: data?.createTargetOrderWithCartAndRegisterUser?.user.id,
            [this.ACCESS_TOKEN]: data?.createTargetOrderWithCartAndRegisterUser?.accessToken,
          });
          this.appService.isSignedIn = true;
          this.appService.clearCart();
          this.authenticated.next(true);
          return data?.createTargetOrderWithCartAndRegisterUser;
        }),
      );
  }

  createTargetOrderByCart(input: MarketPlaceOrderInput): Observable<SuccessResponseWithOrderIdType> {
    return this.createTargetOrderByCartGQL
      .mutate({
        target: { pos: this.storageHelper.getData('posId') },
         input: {
          ...input,
          currency: {
            id: '651afcdde5ecd0565a7b0f91',
            name: 'TND'
          }
        },
      })
      .pipe(
        rxMap(({ data }: any) => {
          this.productCart.next({
            ...this.productCart.value,
            products: null,
          });
          this.selectedSpecs.next([]);
          return data.createTargetOrderByCart;
        })
      );
  }

  sendOrderByEmailAndSms(orderId: string): Observable<SuccessResponseDtoType> {
    return this.sendOrderByEmailAndSmsGQL
      .fetch({
        orderId,
        subject: 'Confirmation de commande : Votre achat a été confirmé !',
        target: { pos: this.storageHelper.getData('posId') },
      })
      .pipe(
        rxMap(({ data }: any) => {
          if (data) {
            return data.sendOrderByEmailAndSms;
          }
        }),
      );
  }

  findDeliveryZonesByTarget(): Observable<DeliveryZonesType[]> {
    return this.findDeliveryZonesByTargetGQL
      .fetch({
        target: { pos: this.storageHelper.getData('posId') },
      })
      .pipe(
        rxMap(({ data }: any) => {
          if (data) {
            this.deliveryZones.next(data.findDeliveryZonesByTargetCache);
            return data.findDeliveryZonesByTargetCache;
          }
        }),
      );
  }

  getOrderSettings(): Observable<OrderSettingsFullType> {
    return this.getOrderSettingsByTargetGQL
      .fetch({
        target: { pos: this.storageHelper.getData('posId') },
      })
      .pipe(
        tap((response: any) => {
          if (response.data) {
            this.orderSettings.next(response.data.getOrderSettingsByTargetCache);
            return response.data.getOrderSettingsByTargetCache;
          }
        }),
      );
  }

  createTargetOrderWithCartForGuestUser(input) {
    return this.createTargetOrderWithCartForGuestUserGql
      .mutate({
        input: {
          ...input,
          target: {
            pos: this.storageHelper.getData('posId'),
          },
        },
      })
      .pipe(
        take(1),
        rxMap(({ data }: any) => {
          this.productCart.next({
            ...this.productCart.value,
            products: null,
          });
          this.selectedSpecs.next([]);
          this.orders.next([data.createTargetOrderWithCartForGuestUser, ...(this.orders.value || [])]);
          return data.createTargetOrderWithCartForGuestUser;
        }),
      );
  }

  emptyTargetCart(): Observable<ShoppingCartType> {
    return this.emptyTargetCartGQL.mutate({ target: { pos: this.storageHelper.getData('posId') } }).pipe(
      rxMap(({ data }: any) => {
        this.productCart.next(data.addToCart);
        return data.emptyTargetCart;
      }),
    );
  }

  addProductsToCart(products: CartProductInput[]): Observable<ShoppingCartType> {
    return this.addProductsToCartGQL.mutate({ products, target: { pos: this.storageHelper.getData('posId') } }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          this.selectedSpecs.next(
          reduce(
            data?.addProductsToCart.products,
            (accumulator, current) => {
              return [...accumulator, ...map(current?.barcode?.internalProduct?.specs, 'value')];
            },
            [],
          ),
        );
          this.productCart.next(data.addProductsToCart);
          return data.addProductsToCart;
        }
      }),
    );
  }

  findShoppingCartByUserAndTarget(pos: string): Observable<ShoppingCartType> {
    return this.findShoppingCartByUserAndTargetGQL.fetch({ target: { pos } }, { fetchPolicy: 'network-only' }).pipe(
      rxMap(({ data }: any) => {
        this.productCart.next(data?.findShoppingCartByUserAndTarget);
        this.selectedSpecs.next(
          reduce(
            data?.findShoppingCartByUserAndTarget.products,
            (accumulator, current) => {
              return [...accumulator, ...map(current?.barcode?.internalProduct?.specs, 'value')];
            },
            [],
          ),
        );
        return data?.findShoppingCartByUserAndTarget;
      }),
    );
  }

  removeFromCart(pos: string, product: CartProductInput): Observable<ShoppingCartType> {
    return this.removeFromCartGQL.mutate({ target: { pos }, product }).pipe(
      rxMap(({ data }: any) => {
        this.productCart.next(data.removeFromCart);
        this.selectedSpecs.next(
          reduce(
            data?.removeFromCart.products,
            (accumulator, current) => {
              return [...accumulator, ...map(current?.barcode?.internalProduct?.specs, 'value')];
            },
            [],
          ),
        );
        return data.removeFromCart;
      }),
    );
  }

  emptyCart(): Observable<ShoppingCartType> {
    return this.emptyCartGQL.mutate().pipe(
      rxMap(({ data }: any) => {
        this.productCart.next(data.emptyCart);
        this.selectedSpecs.next([]);
        return data.emptyCart;
      }),
    );
  }

  addDiscountToCart(id: string, input: DiscountDtoInput): Observable<ShoppingCartType> {
    return this.addDiscountToCartGQL.mutate({ input, id }).pipe(
      rxMap(({ data }: any) => {
        this.productCart.next(data.addDiscountToCart);
        return data.addDiscountToCart;
      }),
    );
  }

  cancelList(id: string): Observable<MarketPlaceOrderDtoType> {
    return this.orders.pipe(
      take(1),
      rxMap((orders) => {
        const order = orders.find((item) => item.id === id) || null;
        this.order.next(order);
        return order;
      }),
      switchMap((order: any) => {
        if (!order) {
          // return throwError(() => new Error('Could not found order with id of ' + id + '!'));
        }
        forEach(order.shoppingCart.products, (product) => {
          this.cancelProductByConsumerGQL.mutate({ input: { orderId: order.id, barcodeId: product.barcode.id } }).subscribe((res: any) => {
            if (res.data) {
              order = res.data.cancelProductByConsumer;
              const index = findIndex(this.orders.value, { id: order.id });
              this.orders.next([...slice(this.orders.value, 0, index), res.data.cancelProductByConsumer, ...slice(this.orders.value, index + 1)]);
            }
          });
        });
        return of(order);
      }),
    );
  }

  cancelProduct(input: MarketPlaceOrderProductInput): Observable<MarketPlaceOrderDtoType> {
    return this.cancelProductByConsumerGQL.mutate({ input }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          const index = findIndex(this.orders.value, { id: input.orderId });
          this.orders.next([...slice(this.orders.value, 0, index), data.cancelProductByConsumer, ...slice(this.orders.value, index + 1)]);
          this.order.next(data.cancelProductByConsumer);
          return data.cancelProductByConsumer;
        }
      }),
    );
  }

  calculateShoppingCart(products: CartProductInput[]): Observable<CalculatedShoppingCartType> {
    return this.calculateShoppingCartGQL.fetch({ products }).pipe(
      rxMap(({ data }: any) => {
        if (data) {
          this.calculatedShoppingCart.next(data.calculateShoppingCart);
          return data.calculateShoppingCart;
        }
      }),
    );
  }

  getTargetOrderInvoice(orderId: string): Observable<InvoicePdfType> {
    return this.getTargetOrderInvoiceGQL.fetch({ orderId }).pipe(
      rxMap((response: any) => {
        if (response.data) {
          return response.data.getTargetOrderInvoice;
        }
      }),
    );
  }

  findMarketPlaceOrdersByUserAndTarget(fetchPolicy: FetchPolicy = 'network-only'): Observable<MarketPlaceOrderPaginateType[]> {
    return this.findTargetOrdersByCurrentUserGQL
      .fetch(
        {
          pagination: { page: this.orderPage, limit: this.orderLimit },
          target: { pos: this.storageHelper.getData('posId') },
        },
        { fetchPolicy },
      )
      .pipe(
        rxMap(({ data }: any) => {
          if (data) {
            this.orders.next(data.findTargetOrdersByCurrentUser.objects);
            this.pagination.next({ page: this.orderPage, size: this.orderLimit, length: data.findTargetOrdersByCurrentUser.count });
            return data.findTargetOrdersByCurrentUser;
          }
        }),
      );
  }

  redeemCoupon(coupon: string): Observable<CouponType> {
    return this.redeemCouponGQL.mutate({ target: { pos: this.storageHelper.getData('posId') }, couponCode: coupon }).pipe(
      rxMap(({ data }: any) => {
        return data.redeemCoupon;
      }),
    );
  }

  findCouponByTargetAndCode(pos: string, couponCode: string): Observable<CouponType> {
    return this.findCouponByTargetAndCodeGQL.fetch({ target: { pos }, couponCode }).pipe(
      rxMap(({ data }: any) => {
        return data.findCouponByTargetAndCode;
      }),
    );
  }

  isCouponValid(couponCode: string): Observable<IsCouponValidType> {
    return this.isCouponValidGQL.fetch({ target: { pos: this.storageHelper.getData('posId') }, couponCode }).pipe(
      rxMap(({ data }: any) => {
        return data.isCouponValid;
      }),
    );
  }

  findTargetOrdersByCurrentUser(input: MarketPlaceOrderFilterConsumerInput, pagination?: PaginationInput): Observable<InternalProductType[]> {
    return this.posService.pos$.pipe(
      take(1),
      switchMap((pos) =>
        this.findTargetOrdersByCurrentUserGQL.fetch({ input, target: { pos: pos.id }, pagination }).pipe(
          tap((response: any) => {
            this.products.next(response.data.findTargetOrdersByCurrentUser.objects);
            this.pagination.next({
              length: response.data.findTargetOrdersByCurrentUser.count,
              size: pagination?.limit ? pagination.limit : 10,
              page: pagination?.page ? pagination.page : 0,
            });
            return this.products.value;
          }),
        ),
      ),
    );
  }

  getOrderInstallment(orderId: string, installmentId: string): Observable<InternalProductType[]> {
    return this.posService.pos$.pipe(
      take(1),
      switchMap((pos) =>
        this.getOrderInstallmentGQL.fetch({ orderId, installmentId }).pipe(
          tap((response: any) => {
            return response.data.getOrderInstallment;
          }),
        ),
      ),
    );
  }

  getOrderById(id: string): Observable<MarketPlaceOrderDtoType> {
    return this.orders.pipe(
      take(1),
      rxMap((orders) => {
        const order = orders.find((item) => item.id === id) || null;
        this.order.next(order);
        return order;
      }),
      switchMap((order) => {
        if (!order) {
          throwError(() => new Error('Could not found order with id of ' + id + '!'));
        }
        return of(order);
      }),
    );
  }

  createMarketPlaceOrder(input: MarketPlaceOrderInput): Observable<MarketPlaceOrderDtoType> {
    return this.createMarketPlaceOrderGQL.mutate({ input }).pipe(
      tap((response: any) => {
        if (response.data) {
          this.order.next(response.data.createMarketPlaceOrder);
          return response.data.createMarketPlaceOrder;
        }
      }),
    );
  }

  cancelProductByConsumer(input: MarketPlaceOrderProductInput): Observable<MarketPlaceOrderDtoType> {
    return this.cancelProductByConsumerGQL.mutate({ input }).pipe(
      tap((response: any) => {
        if (response.data) {
          this.order.next(response.data.cancelProductByConsumer);
          return response.data.cancelProductByConsumer;
        }
      }),
    );
  }

  payInstallments(input: InstallmentPaymentInput): Observable<MarketPlaceOrderDtoType> {
    return this.payMarketplaceOrderInstallmentsGQL.mutate({ input }).pipe(
      tap((response: any) => {
        if (response.data) {
          return response.data.payInstallments;
        }
      }),
    );
  }
}
